import { FC } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer } from "@/components/ui/chart-container";
import { formatCurrency } from "@/utils/format";

/** Data point for bar chart - name field required for x-axis, other fields are numeric values */
export interface BarChartDataPoint {
  name: string;
  [key: string]: string | number;
}

/** Tooltip payload props from Recharts */
interface TooltipPayloadProps {
  payload: BarChartDataPoint;
  dataKey: string;
}

interface BaseBarChartProps {
  data: BarChartDataPoint[];
  dataKeys: string[]; // Keys for the bars
  colors: string[]; // Colors for the bars
  xAxisKey?: string; // Key for the X-axis
  formatter?: (value: number | string) => string; // Formatter for Y-axis and tooltip values
  legendTitle?: string; // Optional legend title for additional context
  tooltipFormatter?: (value: number, name: string, props: TooltipPayloadProps) => string;
  margin?: { top: number; right: number; bottom: number; left: number };
  xAxis?: {
    angle?: number;
    textAnchor?: string;
    interval?: number;
    fontSize?: number;
    dy?: number;
  };
  yAxis?: {
    width?: number;
    tickFormatter?: (value: number) => string;
  };
}

export const BaseBarChart: FC<BaseBarChartProps> = ({
  data,
  dataKeys,
  colors,
  xAxisKey = "name",
  formatter = formatCurrency,
  legendTitle = "Legend", // Default value for legend title
  tooltipFormatter,
  margin = { top: 20, right: 20, bottom: 20, left: 20 },
  xAxis = {},
  yAxis = {},
}) => {
  return (
    <ChartContainer aria-label="Bar chart visualization">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={margin} aria-label="Responsive bar chart">
          {/* Grid lines for chart readability */}
          <CartesianGrid strokeDasharray="3 3" />
          {/* X-Axis */}
          <XAxis
            dataKey={xAxisKey}
            interval={xAxis.interval ?? 0}
            angle={xAxis.angle ?? 0}
            textAnchor={xAxis.textAnchor ?? "middle"}
            height={xAxis.angle ? 60 : 30}
            tick={{
              fontSize: xAxis.fontSize ?? 12,
              dy: xAxis.dy ?? 0,
            }}
            label={{ value: "Categories", position: "insideBottom", offset: -5 }}
            tickLine={false}
            axisLine={true}
          />
          {/* Y-Axis */}
          <YAxis
            width={yAxis.width ?? 40}
            tickFormatter={yAxis.tickFormatter ?? formatter}
            label={{
              value: "Values",
              angle: -90,
              position: "insideLeft",
            }}
            tick={{ fontSize: 12 }}
          />
          {/* Tooltip */}
          <Tooltip
            formatter={(value: number, name: string, props: TooltipPayloadProps) => {
              if (tooltipFormatter) {
                return tooltipFormatter(value, name, props);
              }
              return [formatter(value), name];
            }}
            labelFormatter={(label) => label || ''}
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '4px',
              padding: '8px',
              fontSize: '12px',
              fontFamily: 'inherit'
            }}
            wrapperStyle={{ outline: 'none' }}
            cursor={{ fill: 'transparent' }}
          />
          {/* Legend */}
          <Legend
            payload={dataKeys.map((key, index) => ({
              id: key,
              type: "square",
              value: key,
              color: colors[index],
            }))}
            verticalAlign="top"
            height={36}
            iconType="circle"
            iconSize={8}
          >
            <div aria-label="Legend title">{legendTitle}</div>
          </Legend>
          {/* Bars */}
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[index]}
              radius={[4, 4, 0, 0]}
              aria-label={`Bar representing ${key}`}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};